'use client';

import React, { useState } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { FaEye, FaEyeSlash } from 'react-icons/fa';
import { signIn } from 'next-auth/react';
import { FcGoogle } from 'react-icons/fc';
import { RippleButton } from '../RippleButton';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@/components/ui/dialog';

interface LoginPopupProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  /** Where the user should end up after any full-page auth redirect (Google, sign-up). */
  callbackUrl?: string;
  /**
   * Called before the user leaves the page for Google sign-in or the sign-up page,
   * so the caller can persist any unsaved work (e.g. a card draft).
   */
  onBeforeRedirect?: () => Promise<void> | void;
  /** Explanatory line under the title. */
  description?: string;
}

export default function LoginPopup({
  isOpen,
  onClose,
  onSuccess,
  callbackUrl = '/',
  onBeforeRedirect,
  description = 'Please log in to create and save cards.',
}: LoginPopupProps) {
  const router = useRouter();
  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });
  const [errors, setErrors] = useState({
    email: '',
    password: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loginError, setLoginError] = useState('');
  
  // Use useEffect to handle successful login state
  const [loginSuccessful, setLoginSuccessful] = useState(false);
  
  // Handle successful login with useEffect to ensure it runs before component unmounts
  React.useEffect(() => {
    if (loginSuccessful && onSuccess) {
      onSuccess();
      setLoginSuccessful(false);
    }
  }, [loginSuccessful, onSuccess]);

  const handleGoogleSignIn = async () => {
    try {
      setIsSubmitting(true);
      // Google is a full-page OAuth redirect: let the caller save its work first,
      // then come back to callbackUrl (which should restore that work).
      if (onBeforeRedirect) await onBeforeRedirect();
      await signIn('google', { callbackUrl });
    } catch (error) {
      setLoginError('Google login failed. Please try again.');
      console.error('Google login error:', error);
      setIsSubmitting(false);
    }
  };

  const handleSignUpClick = async (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    try {
      if (onBeforeRedirect) await onBeforeRedirect();
    } catch (error) {
      console.error('Failed to save before sign-up redirect:', error);
    }
    onClose();
    router.push(registerHref);
  };

  const registerHref = `/auth/register?callbackUrl=${encodeURIComponent(callbackUrl)}`;

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    // Clear error when user types
    if (name in errors && errors[name as keyof typeof errors]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    let valid = true;
    const newErrors = { ...errors };

    // Email validation
    if (!formData.email) {
      newErrors.email = 'Email is required.';
      valid = false;
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email.';
      valid = false;
    }

    // Password validation
    if (!formData.password) {
      newErrors.password = 'Password is required.';
      valid = false;
    } else if (formData.password.length < 6) {
      newErrors.password = 'Password must be at least 6 characters.';
      valid = false;
    }

    setErrors(newErrors);
    return valid;
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoginError('');

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      // Use NextAuth.js signIn with credentials
      const result = await signIn('credentials', {
        redirect: false,
        email: formData.email,
        password: formData.password,
        callbackUrl,
      });

      if (result?.error) {
        setLoginError('Login failed. Please check your credentials.');
      } else if (result?.url) {
        // Set login successful state instead of directly calling onSuccess
        setLoginSuccessful(true);
        if (!onSuccess) {
          router.push(result.url);
        }
      }
    } catch (error) {
      setLoginError('Login failed. Please check your credentials.');
      console.error('Login error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword((prev) => !prev);
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent className="bg-white w-[95vw] max-w-md p-6 max-h-[90vh] overflow-y-auto">
        <div className="mb-4 pr-8">
          <DialogTitle className="text-xl font-bold text-primary-color">Log in to continue</DialogTitle>
        </div>

        <DialogDescription className="mb-4 text-gray-600 text-base">{description}</DialogDescription>

        {/* Login Form */}
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Email field */}
          <div>
            <label htmlFor="popup-email" className="block text-sm font-medium text-gray-700 mb-1">
              Email
            </label>
            <input
              type="email"
              id="popup-email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              className={`input-field-custom ${
                errors.email ? 'input-field-custom--error' : 'input-field-custom--default'
              }`}
              placeholder="Enter your email"
              autoComplete="email"
              aria-invalid={!!errors.email}
              aria-describedby={errors.email ? 'popup-email-error' : undefined}
            />
            {errors.email && <p id="popup-email-error" className="input-error-msg">{errors.email}</p>}
          </div>

          {/* Password field */}
          <div>
            <label htmlFor="popup-password" className="block text-sm font-medium text-gray-700 mb-1">
              Password
            </label>
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                id="popup-password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                className={`input-field-custom ${
                  errors.password ? 'input-field-custom--error' : 'input-field-custom--default'
                }`}
                placeholder="Enter your password"
                autoComplete="current-password"
                aria-invalid={!!errors.password}
                aria-describedby={errors.password ? 'popup-password-error' : undefined}
              />
              <button
                type="button"
                onClick={togglePasswordVisibility}
                className="absolute inset-y-0 right-0 pr-3 flex items-center"
                aria-label={showPassword ? 'Hide password' : 'Show password'}
                aria-pressed={showPassword}
              >
                {showPassword ? (
                  <FaEyeSlash className="text-gray-500" />
                ) : (
                  <FaEye className="text-gray-500" />
                )}
              </button>
            </div>
            {errors.password && <p id="popup-password-error" className="input-error-msg">{errors.password}</p>}
          </div>

          {/* Forgot password link */}
          <div className="flex justify-end">
            <Link href="/auth/forget-password" className="text-sm text-primary-color hover:underline" onClick={onClose}>
              Forgot password?
            </Link>
          </div>

          {/* Login error message */}
          {loginError && (
            <div role="alert" className="p-3 bg-red-100 border border-red-400 text-red-700 rounded">
              {loginError}
            </div>
          )}

          {/* Submit button */}
          <RippleButton
            type="submit"
            disabled={isSubmitting}
            className="w-full"
          >
            {isSubmitting ? 'Signing in...' : 'Sign In'}
          </RippleButton>
        </form>

        {/* Divider */}
        <div className="relative flex items-center justify-center my-6">
          <div className="border-t border-gray-300 w-full"></div>
          <span className="bg-white px-3 text-sm text-gray-600 absolute">or continue with</span>
        </div>

        {/* Google login button */}
        <RippleButton
          type="button"
          variant={'outline'}
          onClick={handleGoogleSignIn}
          disabled={isSubmitting}
          className="flex items-center justify-center gap-2 w-full py-2 px-4 text-gray-700 hover:bg-gray-50 transition duration-300 disabled:opacity-70"
        >
          <FcGoogle size={20} />
          <span>Sign in with Google</span>
        </RippleButton>

        {/* Sign up prompt */}
        <p className="text-center text-gray-600 mt-6">
          Don't have an account?{' '}
          <Link href={registerHref} className="text-primary-color hover:underline" onClick={handleSignUpClick}>
            Sign up
          </Link>
        </p>
      </DialogContent>
    </Dialog>
  );
}